/** The FOMO social layer (docs/API.md "Social"): activity tape, reactions, token trades, push, share cards. */
import { z } from 'zod'
import { Address, Page } from '../../primitives.js'
import { Ok } from '../common.js'
import {
  ActivityItem,
  ActivityQuery,
  PostReactions,
  PostReactionsQuery,
  PushSubscription,
  PushSubscriptionInput,
  PushSubscriptionRef,
  ReactionBody,
  TokenTrades,
  TokenTradesQuery,
  VapidKey,
} from '../social.js'
import { HandleParams, IdParams } from './params.js'
import { defineRoute, type RouteDef } from './define.js'

export const socialRoutes = {
  listActivity: defineRoute({ method: 'GET', path: '/activity', auth: 'public', summary: 'Live activity tape, newest first', query: ActivityQuery, response: Page(ActivityItem) }),
  streamActivity: defineRoute({
    method: 'GET',
    path: '/activity/stream',
    auth: 'public',
    summary: 'SSE of ActivityItem (event `activity`, id = seq; Last-Event-ID resumes), heartbeat every 15 s',
    response: ActivityItem,
    stream: true,
  }),
  getPostReactions: defineRoute({
    method: 'GET',
    path: '/posts/reactions',
    auth: 'public',
    summary: 'Reaction counts (and the caller’s own) for up to 100 posts',
    query: PostReactionsQuery,
    response: z.array(PostReactions),
  }),
  addPostReaction: defineRoute({ method: 'POST', path: '/posts/:id/reactions', auth: 'session', summary: 'React to a post (idempotent)', params: IdParams, body: ReactionBody, response: PostReactions }),
  removePostReaction: defineRoute({ method: 'DELETE', path: '/posts/:id/reactions', auth: 'session', summary: 'Take a reaction back (idempotent)', params: IdParams, body: ReactionBody, response: PostReactions }),
  getTokenTrades: defineRoute({
    method: 'GET',
    path: '/tokens/:address/trades',
    auth: 'public',
    summary: 'Recent buys and sells of an agent token, holder count and 24 h delta',
    params: z.object({ address: Address }),
    query: TokenTradesQuery,
    response: TokenTrades,
  }),
  getVapidKey: defineRoute({ method: 'GET', path: '/push/vapid-key', auth: 'public', summary: 'Web push application server key', response: VapidKey }),
  createPushSubscription: defineRoute({
    method: 'POST',
    path: '/me/push-subscriptions',
    auth: 'session',
    summary: 'Register this browser for web push (upsert by endpoint)',
    body: PushSubscriptionInput,
    status: 201,
    response: PushSubscription,
  }),
  deletePushSubscription: defineRoute({ method: 'DELETE', path: '/me/push-subscriptions', auth: 'session', summary: 'Unregister a browser', body: PushSubscriptionRef, response: Ok }),
  listPushSubscriptions: defineRoute({ method: 'GET', path: '/me/push-subscriptions', auth: 'session', summary: 'Browsers registered for web push', response: z.array(PushSubscription) }),
  getAgentCard: defineRoute({
    method: 'GET',
    path: '/agents/:handle/card.png',
    auth: 'public',
    summary: 'P&L share card (1200×630 PNG, cached)',
    params: HandleParams,
    responseType: 'png',
    response: z.string(),
  }),
} as const satisfies Record<string, RouteDef>
